import clsx from 'clsx'
import React from 'react'

type TypographyWeight =
  | '100'
  | '200'
  | '300'
  | '400'
  | '500'
  | '600'
  | '700'
  | '800'
  | '900'
export type TypographyVariant =
  | 'bodyMedium'
  | 'bodySmall'
  | 'bodyXSmall'
  | 'buttonSmall'
  | 'buttonSmall1'
  | 'buttonMedium'
  | 'buttonLarge'
  | 'headersH2'
  | 'headersH1'
  | 'headersH3'
  | 'headersH4'
  | 'headersH5'
  | 'headersDispalyXSmall'
  | 'headersDispalySmall'
  | 'headersDispalyLarge'
  | 'bodyLarge'
  | 'bodyLarge1'

type TypographyType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'

type Props = {
  weight?: TypographyWeight
  variant: TypographyVariant
  type: TypographyType
  className?: string
  children: React.ReactNode
  style?: React.CSSProperties
}

const typeStyles: Record<TypographyVariant, string> = {
  headersH1: clsx(
    'p-0',
    'text-[18px] leading-[40px] tracking-[0px]', // mobile
    'sm:text-[36px] sm:leading-[52px] sm:tracking-[0px]', // tablet
    'md:text-[40px] md:leading-[60px] md:tracking-[0px]', // desktop
  ),
  headersH2: clsx(
    'p-0',
    'text-[24px] leading-[36px] tracking-[0px]', // mobile
    'sm:text-[28px] sm:leading-[40px] sm:tracking-[0px]', // tablet
    'md:text-[32px] md:leading-[48px] md:tracking-[0px]', // desktop
  ),
  headersH3: clsx(
    'p-0',
    'text-[20px] leading-[21px] tracking-[0px]', // mobile
    'sm:text-[24px] sm:leading-[32px] sm:tracking-[0px]', // tablet
    'md:text-[28px] md:leading-[40px] md:tracking-[0px]', // desktop
  ),
  headersH4: clsx(
    'p-0',
    'text-[18px] leading-[27px] tracking-[0px]', // mobile
    'sm:text-[20px] sm:leading-[30px] sm:tracking-[0px]', // tablet
    'md:text-[24px] md:leading-[36px] md:tracking-[0px]', // desktop
  ),
  headersH5: clsx(
    'p-0',
    'text-[14px] leading-[27px] tracking-[0px]', // mobile
    'sm:text-[20px] sm:leading-[30px] sm:tracking-[0px]', // tablet
    'md:text-[18px] md:leading-[27px] md:tracking-[0px]', // desktop
  ),
  headersDispalyXSmall: clsx(
    'p-0',
    'text-[36px] leading-[48px] tracking-[0px]', // mobile
    'sm:text-[36px] sm:leading-[54px] sm:tracking-[0px]', // tablet
    'md:text-[48px] md:leading-[66px] md:tracking-[0px]', // desktop
  ),
  headersDispalySmall: clsx(
    'p-0',
    'text-[36px] leading-[48px] tracking-[0px]', // mobile
    'sm:text-[44px] sm:leading-[63px] sm:tracking-[0px]', // tablet
    'md:text-[48px] md:leading-[60px] md:tracking-[0px]', // desktop
  ),
  headersDispalyLarge: clsx(
    'p-0',
    'text-[26px] leading-[48px] tracking-[0px]', // mobile
    'sm:text-[52px] sm:leading-[63px] sm:tracking-[0px]', // tablet
    'md:text-[60px] md:leading-[60px] md:tracking-[0px]', // desktop
  ),
  buttonLarge: clsx(
    'p-0',
    'text-[16px] leading-[24px] tracking-[-0.24px]', // mobile
    'sm:text-[16px] sm:leading-[24px] sm:tracking-[-0.24px]', // tablet
    'md:text-[18px] md:leading-[28px] md:tracking-[0px]', // desktop
  ),
  buttonMedium: clsx(
    'p-0',
    'text-[14px] leading-[21px] tracking-[-0.24px]', // mobile
    'sm:text-[14px] sm:leading-[21px] sm:tracking-[-0.24px]', // tablet
    'md:text-[16px] md:leading-[24px] md:tracking-[0px]', // desktop
  ),
  buttonSmall: clsx(
    'p-0',
    'text-[12px] leading-[18px] tracking-[-0.24px]', // mobile
    'sm:text-[12px] sm:leading-[18px] sm:tracking-[-0.24px]', // tablet
    'md:text-[14px] md:leading-[21px] md:tracking-[-0.24px]', // desktop
  ),
  buttonSmall1: clsx(
    'p-0',
    'text-[10px] leading-[18px] tracking-[-0.24px]', // mobile
    'sm:text-[12px] sm:leading-[18px] sm:tracking-[-0.24px]', // tablet
    'md:text-[14px] md:leading-[21px] md:tracking-[-0.24px]', // desktop
  ),
  bodyMedium: clsx(
    'p-0',
    'tracking-[-0.24px]',
    'text-[15px] leading-[22px]', // mobile
    'sm:text-[16px] sm:leading-[24px]', // tablet
    'md:text-[16px] md:leading-[24px]', // desktop
  ),
  bodySmall: clsx(
    'p-0',
    'tracking-[-0.24px]',
    'text-[14px] leading-[21px]', // mobile
    'sm:text-[14px] sm:leading-[21px]', // tablet
    'md:text-[14px] md:leading-[21px]', // desktop
  ),
  bodyXSmall: clsx(
    'p-0',
    'tracking-[-0.24px]',
    'text-[12px] leading-[18px]', // mobile
    'sm:text-[12px] sm:leading-[18px]', // tablet
    'md:text-[12px] md:leading-[18px]', // desktop
  ),
  bodyLarge: clsx(
    'p-0',
    'tracking-[-0.24px]',
    'text-[16px] leading-[24px]', // mobile
    'sm:text-[18px] sm:leading-[27px]', // tablet
    'md:text-[18px] md:leading-[27px]', // desktop
  ),
  bodyLarge1: clsx(
    'p-0',
    'tracking-[-0.24px]',
    'text-[16px] leading-[24px]', // mobile
    'sm:text-[18px] sm:leading-[27px]', // tablet
    'md:text-[20px] md:leading-[27px]', // desktop
  ),
}

const Typography: React.FC<Props> = ({
  weight = '400',
  type,
  variant,
  className,
  children,
  style = {},
}) => {
  const fontWeight = `font-[${weight}]`

  switch (type) {
    case 'h1':
      return (
        <h1
          className={clsx(typeStyles[variant], className, fontWeight)}
          style={style}
        >
          {children}
        </h1>
      )
    case 'h2':
      return (
        <h2
          className={clsx(typeStyles[variant], className, fontWeight)}
          style={style}
        >
          {children}
        </h2>
      )
    case 'h3':
      return (
        <h3
          className={clsx(typeStyles[variant], className, fontWeight)}
          style={style}
        >
          {children}
        </h3>
      )
    case 'h4':
      return (
        <h4
          className={clsx(typeStyles[variant], className, fontWeight)}
          style={style}
        >
          {children}
        </h4>
      )
    case 'h5':
      return (
        <h5
          className={clsx(typeStyles[variant], className, fontWeight)}
          style={style}
        >
          {children}
        </h5>
      )
    case 'h6':
      return (
        <h6
          className={clsx(typeStyles[variant], className, fontWeight)}
          style={style}
        >
          {children}
        </h6>
      )
    case 'p':
      return (
        <p
          className={clsx(
            typeStyles[variant],
            className,
            fontWeight,
            // 'break-words'
          )}
          style={style}
        >
          {children}
        </p>
      )
    case 'span':
      return (
        <span
          className={clsx(typeStyles[variant], className, fontWeight)}
          style={style}
        >
          {children}
        </span>
      )

    default:
      break
  }
}

export default Typography
